// TopUpModalV3 — placeholder modal opened when the user clicks the credits
// stat card or its low-balance badge. Two mock packages; the first is a
// real Stripe checkout via `api.createCheckout("starter_1000")` (already
// wired backend-side since Phase 0). Fallback: shows the packages as
// non-clickable if the API call fails.
import React, { useState } from "react";
import { X, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function TopUpModalV3({ open, onClose }) {
    const { t } = useTranslation("dashboard_v3");
    const [busy, setBusy] = useState(false);

    const buyStarter = async () => {
        setBusy(true);
        try {
            const { url } = await api.createCheckout("starter_1000");
            window.location.href = url;
        } catch (e) {
            toast.error(e.message || "Checkout non disponibile.");
            setBusy(false);
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="fixed inset-0 z-[70] flex items-center justify-center p-4"
                    data-testid="dashboard-v3-topup-modal"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="topup-title"
                >
                    <div
                        className="absolute inset-0"
                        style={{ backgroundColor: "rgba(0,0,0,0.72)" }}
                        onClick={onClose}
                    />
                    <motion.div
                        initial={{ scale: 0.96, y: 8 }}
                        animate={{ scale: 1, y: 0 }}
                        exit={{ scale: 0.96, y: 8 }}
                        transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
                        className="relative w-full max-w-md rounded-2xl border p-6"
                        style={{ backgroundColor: "#111111", borderColor: "#1A1A1A" }}
                    >
                        <button
                            type="button"
                            onClick={onClose}
                            aria-label={t("topup_modal.close")}
                            className="absolute top-4 right-4 h-8 w-8 rounded-lg grid place-items-center text-white/70 hover:text-white hover:bg-white/[0.06]"
                            data-testid="dashboard-v3-topup-close"
                        >
                            <X className="w-4 h-4" />
                        </button>
                        <h2 id="topup-title" className="text-lg font-semibold text-[#FFFFFF]">
                            {t("topup_modal.title")}
                        </h2>
                        <p className="mt-1 text-sm text-[#CCCCCC]">
                            {t("topup_modal.description")}
                        </p>

                        <div className="mt-6 space-y-3">
                            {/* Starter package — real Stripe checkout */}
                            <div
                                className="rounded-xl border p-4 flex items-center justify-between"
                                style={{ backgroundColor: "#0A0A0A", borderColor: "#1A1A1A" }}
                            >
                                <div>
                                    <p className="text-sm font-semibold text-[#FFFFFF]">
                                        {t("topup_modal.starter_credits")}
                                    </p>
                                    <p className="mt-0.5 text-xs text-[#CCCCCC]">
                                        {t("topup_modal.starter_price")}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={buyStarter}
                                    disabled={busy}
                                    data-testid="dashboard-v3-topup-starter-cta"
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-white transition-transform hover:scale-[0.98] disabled:opacity-60"
                                    style={{ backgroundColor: "#FFFFFF" }}
                                >
                                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                                    {t("topup_modal.starter_cta")}
                                </button>
                            </div>
                            {/* Pro package — placeholder (redirect to /pricing) */}
                            <a
                                href="/app/subscription"
                                className="rounded-xl border p-4 flex items-center justify-between no-underline hover:border-[#FFFFFF] transition-colors"
                                style={{ backgroundColor: "#0A0A0A", borderColor: "#1A1A1A" }}
                                data-testid="dashboard-v3-topup-pro-cta"
                            >
                                <div>
                                    <div className="flex items-center gap-2">
                                        <p className="text-sm font-semibold text-[#FFFFFF]">
                                            {t("topup_modal.pro_credits")}
                                        </p>
                                        <span
                                            className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full"
                                            style={{
                                                backgroundColor: "rgba(255, 255, 255,0.15)",
                                                color: "#FFFFFF",
                                            }}
                                        >
                                            {t("topup_modal.pro_badge")}
                                        </span>
                                    </div>
                                    <p className="mt-0.5 text-xs text-[#CCCCCC]">
                                        {t("topup_modal.pro_price")}
                                    </p>
                                </div>
                                <span
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-white transition-transform hover:scale-[0.98]"
                                    style={{ backgroundColor: "#FFFFFF" }}
                                >
                                    {t("topup_modal.pro_cta")}
                                </span>
                            </a>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
